import { useState, useEffect, useRef } from 'react';
import {
    Users, FileCheck2, QrCode, Wallet, BarChart3,
    CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, Sparkles, Target,
    Briefcase, Settings, Bell, Smartphone, ArrowDown, Check, ArrowRight
} from 'lucide-react';

const ROUTES = {
    config: '/configuracion',
    empresas: '/empresas',
    clientes: '/clientes',
    servicios: '/servicios',
    serviciosMes: '/servicios/mes',
    rutas: '/rutas-cobro',
    dashboard: '/',
};

const STEPS = [
    {
        n: 1,
        title: 'Configura tu empresa',
        subtitle: 'Esto sale en cada EDP que generes',
        icon: Settings,
        color: '#3D7A7A',
        actionLabel: 'Ir a Configuración',
        actionRoute: ROUTES.config,
        description:
            'Antes de crear servicios, registra los datos de tu empresa que aparecerán en el PDF del Estado de Pago: nombre legal, RUT, banco, número de cuenta corriente y email para pagos.',
        checklist: [
            'Nombre legal completo (ej: LEGAV SERVICIOS INTEGRALES LIMITADA)',
            'RUT para datos de transferencia',
            'Banco y N° cuenta corriente',
            'Email para envío de comprobantes',
        ],
        tip: 'Estos datos se imprimen al pie de cada EDP. Si los cambias, el siguiente PDF que generes los reflejará automáticamente.',
        mock: 'config',
    },
    {
        n: 2,
        title: 'Carga tus clientes',
        subtitle: 'La cartera de empresas a las que le facturarás',
        icon: Users,
        color: '#5B9A9A',
        actionLabel: 'Ir a Clientes',
        actionRoute: ROUTES.clientes,
        description:
            'Registra cada cliente con su razón social, RUT, dirección y los datos de contacto financiero. Estos contactos serán los que reciban y aprueben los EDPs.',
        checklist: [
            'Razón social y RUT del cliente',
            'Email de cobranza (importante: aquí llegan los EDPs)',
            'Nombre del contacto financiero',
            'Teléfono de contacto',
        ],
        tip: 'El nombre del contacto financiero, el email y el teléfono son los datos que aparecen en cada EDP que generes para ese cliente.',
        mock: 'clientes',
    },
    {
        n: 3,
        title: 'Crea tu primer servicio',
        subtitle: 'Define qué le vas a facturar a cada cliente',
        icon: Briefcase,
        color: '#7BB3B3',
        actionLabel: 'Ir a Servicios',
        actionRoute: ROUTES.servicios,
        description:
            'En "Servicios" haz click en "Nuevo Servicio". Selecciona el cliente, ponle un nombre al producto, indica la faena/sitio (opcional), el monto neto y elige si es único o recurrente mensual.',
        checklist: [
            'Selecciona Cliente',
            'Nombre del producto (ej: "Software Acreditación")',
            'Faena/Sitio (ej: "Mina Los Pelambres")',
            'Monto neto y condición de pago (días)',
            'Marca "Requiere EDP" si necesitas estado de pago',
            'Si es recurrente: define el rango de meses',
        ],
        tip: 'Si marcas "es recurrente", PaySync replica el servicio automáticamente para cada mes del rango. Cualquier cambio futuro se puede aplicar a todos los períodos del grupo.',
        mock: 'servicios',
    },
    {
        n: 4,
        title: 'Genera el EDP en PDF',
        subtitle: 'Tu Estado de Pago profesional con un click',
        icon: FileCheck2,
        color: '#3D7A7A',
        actionLabel: 'Ver Servicios del Mes',
        actionRoute: ROUTES.serviciosMes,
        description:
            'En la lista de Servicios o Servicios del Mes, los servicios que requieren EDP muestran un botón "Ver" en la columna EDP. Al presionarlo, PaySync genera el PDF profesional con folio correlativo, datos de tu empresa, faena, monto, IVA y un código QR único.',
        checklist: [
            'Folio correlativo automático (ej: 0190, 0191...)',
            'Datos de tu empresa al pie (nombre, RUT, banco, cuenta)',
            'Faena, descripción y comentarios del servicio',
            'Cálculo automático de IVA y total',
            'Código QR para aprobación del cliente',
        ],
        tip: 'Cada vez que presionas "Ver", el PDF se regenera con la información más actualizada. Si editaste algo del servicio, se refleja al instante.',
        mock: 'edp',
    },
    {
        n: 5,
        title: 'El cliente escanea el QR',
        subtitle: 'Aprueba o rechaza desde su celular en segundos',
        icon: QrCode,
        color: '#5B9A9A',
        actionLabel: 'Ver demo',
        actionRoute: '/propuesta',
        description:
            'Envías el PDF al cliente por correo. El cliente abre el PDF, escanea el QR con su celular y se abre una página con todos los datos del EDP. Aprueba o rechaza con un solo botón, opcionalmente con un comentario.',
        checklist: [
            'Página optimizada para móvil',
            'Datos del EDP visibles para revisar',
            'Botón verde "Aprobar" / rojo "Rechazar"',
            'Campo de comentario (obligatorio si rechaza)',
            'Una vez confirmado, no se puede cambiar (a menos que tú reabras el período)',
        ],
        tip: 'Si el cliente rechaza, ves su comentario en el detalle del servicio. Si necesita revisar de nuevo, cambia el estado del servicio a "En Curso" y el QR se reactiva.',
        mock: 'aprobacion',
    },
    {
        n: 6,
        title: 'El estado se actualiza solo',
        subtitle: 'PaySync mueve el servicio al siguiente paso',
        icon: Target,
        color: '#7BB3B3',
        actionLabel: 'Ver Rutas de Cobro',
        actionRoute: ROUTES.rutas,
        description:
            'Cuando el cliente aprueba el EDP, el estado del servicio cambia automáticamente a "EDP APROBADO" (verde). Si rechaza, pasa a "EDP RECHAZADO" (rojo) con su comentario en el historial.',
        checklist: [
            'Estado del servicio sincronizado en tiempo real',
            'Notificación visible en la lista de Servicios',
            'Historial completo de quién aprobó y cuándo',
            'Comentario del cliente guardado para auditoría',
        ],
        tip: 'Puedes configurar Rutas de Cobro distintas (ej: "EDP & OC" o "Pago Directo") y asignarlas a cada servicio. Cada ruta tiene su propio flujo de etapas.',
        mock: 'estados',
    },
    {
        n: 7,
        title: 'Registra el pago',
        subtitle: 'Cierra el ciclo cuando recibes el dinero',
        icon: Wallet,
        color: '#3D7A7A',
        actionLabel: 'Abrir Servicios',
        actionRoute: ROUTES.servicios,
        description:
            'Cuando el cliente paga, abre el servicio y registra el pago: fecha, monto, método (transferencia, cheque, etc.) y comprobante opcional. PaySync calcula el saldo pendiente y marca el servicio como "Pagado" si cubriste el total.',
        checklist: [
            'Pagos parciales o totales',
            'Comprobante adjunto (PDF/imagen)',
            'Si pagó después del vencimiento, queda registrado como "recuperado de mora"',
            'Estado se actualiza a "PAGADO" al cubrir 100%',
        ],
        tip: 'Los pagos de servicios que estuvieron +30 días vencidos cuentan en el KPI "Cobranza Recuperada" del dashboard. Es la métrica más importante de tu equipo de cobranza.',
        mock: 'pago',
    },
    {
        n: 8,
        title: 'Mide en el Dashboard',
        subtitle: 'Toda la cartera bajo control',
        icon: BarChart3,
        color: '#5B9A9A',
        actionLabel: 'Abrir Dashboard',
        actionRoute: ROUTES.dashboard,
        description:
            'En el Dashboard ves los KPIs en vivo del período seleccionado: facturación, IVA, por cobrar, cobranza recuperada (>30 días en mora). Cada KPI es clickeable y abre un detalle con todos los servicios que lo componen.',
        checklist: [
            'Facturación del período (clickeable)',
            'IVA del período (clickeable)',
            'Por cobrar / vencidos / en mora',
            'Cobranza recuperada de servicios que estuvieron en mora',
            'Indicadores económicos en vivo (UF, USD, Cobre)',
        ],
        tip: 'Los KPIs son interactivos. Click en "Facturación" abre la lista completa, click en "IVA" muestra todas las facturas que lo componen. Ideal para auditar cualquier cifra al instante.',
        mock: 'dashboard',
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// Hook: typewriter — types a string char by char after a delay
// ─────────────────────────────────────────────────────────────────────────────
const useTypewriter = (text, speed = 35, delay = 0) => {
    const [out, setOut] = useState('');
    useEffect(() => {
        setOut('');
        let i = 0;
        const start = setTimeout(() => {
            const id = setInterval(() => {
                i++;
                setOut(text.slice(0, i));
                if (i >= text.length) clearInterval(id);
            }, speed);
            return () => clearInterval(id);
        }, delay);
        return () => clearTimeout(start);
    }, [text, speed, delay]);
    return out;
};

// Hook: counts from 0 to target value over a duration
const useCounter = (target, duration = 1200, delay = 0) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        setVal(0);
        const start = setTimeout(() => {
            const t0 = performance.now();
            let raf;
            const step = (t) => {
                const p = Math.min(1, (t - t0) / duration);
                setVal(Math.round(target * (1 - Math.pow(1 - p, 3)))); // ease-out cubic
                if (p < 1) raf = requestAnimationFrame(step);
            };
            raf = requestAnimationFrame(step);
            return () => raf && cancelAnimationFrame(raf);
        }, delay);
        return () => clearTimeout(start);
    }, [target, duration, delay]);
    return val;
};

const fmtCLP = (n) => '$' + n.toLocaleString('es-CL');

// ─────────────────────────────────────────────────────────────────────────────
// Animated screencasts — each rebuilds when remounted (key prop)
// ─────────────────────────────────────────────────────────────────────────────
const baseCard = 'bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden';

const MockConfig = () => {
    const fields = [
        ['Nombre Legal', 'LEGAV SERVICIOS INTEGRALES LTDA'],
        ['RUT', '76.952.065-1'],
        ['Banco', 'Banco Estado'],
        ['N° Cuenta Corriente', '123456789'],
        ['Email Pagos', 'pagos@legav.cl'],
    ];
    const [active, setActive] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setActive((a) => (a < fields.length ? a + 1 : a)), 600);
        return () => clearInterval(id);
    }, []);
    return (
        <div className={baseCard}>
            <div className="bg-slate-900 px-4 py-2.5 border-b text-xs font-semibold text-white flex items-center gap-2">
                <Settings size={13} className="text-[#9DD5D5]" /> Configuración › Información para Pagos
            </div>
            <div className="p-5 space-y-3 text-sm">
                {fields.map(([l, v], i) => (
                    <div
                        key={l}
                        className={`flex justify-between items-center border-b border-slate-100 pb-2 transition-all duration-500 ${
                            i < active ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'
                        }`}
                    >
                        <span className="text-slate-500">{l}</span>
                        <span className="text-slate-900 font-medium font-mono text-xs bg-slate-50 px-2 py-1 rounded">
                            {i < active ? v : ''}
                            {i === active - 1 && <span className="inline-block w-1.5 h-3 bg-[#3D7A7A] ml-0.5 animate-pulse" />}
                        </span>
                    </div>
                ))}
                {active >= fields.length && (
                    <div className="flex items-center gap-2 mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 animate-[fadeInUp_.4s_ease-out]">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <span className="text-emerald-800 text-xs font-semibold">Empresa configurada</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const MockClientes = () => {
    const list = [
        { n: 'TecnoSur Ingeniería SpA', r: '76.123.456-7', c: 'María González', e: 'pagos@tecnosur.cl' },
        { n: 'Constructora Andes', r: '78.234.567-8', c: 'Pedro Reyes', e: 'finanzas@andes.cl' },
        { n: 'Logística Pacífico', r: '79.345.678-9', c: 'Camila Soto', e: 'cuentas@pacifico.cl' },
    ];
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setIdx((i) => (i < list.length ? i + 1 : i)), 700);
        return () => clearInterval(id);
    }, []);
    return (
        <div className={baseCard}>
            <div className="bg-slate-900 px-4 py-2.5 border-b text-xs font-semibold text-white flex items-center justify-between">
                <span className="flex items-center gap-2"><Users size={13} className="text-[#9DD5D5]" /> Cartera de Clientes</span>
                <span className="text-[10px] bg-[#3D7A7A] px-2 py-0.5 rounded-full">{idx} cargados</span>
            </div>
            <div className="divide-y">
                {list.map((c, i) => (
                    <div
                        key={i}
                        className={`px-4 py-3 flex items-center justify-between transition-all duration-500 ${
                            i < idx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                        }`}
                        style={{ transitionDelay: `${i * 80}ms` }}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#3D7A7A] to-[#5B9A9A] flex items-center justify-center text-white text-xs font-bold">
                                {c.n.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                            </div>
                            <div>
                                <p className="font-medium text-slate-900 text-sm">{c.n}</p>
                                <p className="text-[10px] text-slate-500">{c.r} · {c.c}</p>
                            </div>
                        </div>
                        <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">
                            Activo
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MockServicios = () => {
    const cliente = useTypewriter('TecnoSur Ingeniería SpA', 40, 200);
    const producto = useTypewriter('Asesoría Técnica de Acreditación', 35, 1300);
    const faena = useTypewriter('Mina Cerro Verde', 45, 2700);
    const monto = useCounter(5420000, 1200, 3700);
    const [showRecurrent, setShowRecurrent] = useState(false);
    const [showEdp, setShowEdp] = useState(false);
    useEffect(() => {
        const t1 = setTimeout(() => setShowEdp(true), 5100);
        const t2 = setTimeout(() => setShowRecurrent(true), 5800);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);
    return (
        <div className={baseCard}>
            <div className="bg-slate-900 px-4 py-2.5 border-b text-xs font-semibold text-white flex items-center justify-between">
                <span className="flex items-center gap-2"><Briefcase size={13} className="text-[#9DD5D5]" /> Nuevo Servicio</span>
                <span className="text-[10px] text-slate-300">Borrador</span>
            </div>
            <div className="p-5 space-y-3 text-sm">
                {[
                    ['Cliente', cliente],
                    ['Producto', producto],
                    ['Faena / Sitio', faena],
                ].map(([l, v]) => (
                    <div key={l}>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">{l}</p>
                        <div className="border-b border-slate-200 py-1.5 text-slate-900 font-medium min-h-[28px]">
                            {v}<span className="inline-block w-1.5 h-3 bg-[#3D7A7A] ml-0.5 animate-pulse" />
                        </div>
                    </div>
                ))}
                <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest">Monto Neto</p>
                    <div className="text-2xl font-bold text-[#3D7A7A] font-mono">{fmtCLP(monto)}</div>
                </div>
                {showEdp && (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 animate-[fadeInUp_.4s_ease-out]">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <span className="text-emerald-800 text-xs font-semibold">Requiere EDP activado</span>
                    </div>
                )}
                {showRecurrent && (
                    <div className="flex items-center gap-2 bg-[#3D7A7A]/10 border border-[#3D7A7A]/30 rounded-lg p-2.5 animate-[fadeInUp_.4s_ease-out]">
                        <Sparkles size={14} className="text-[#3D7A7A]" />
                        <span className="text-[#2D5A5A] text-xs font-semibold">Recurrente: replica para 12 meses</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const MockEDP = () => {
    const monto = useCounter(5420000, 800, 200);
    const iva = useCounter(1029800, 800, 700);
    const total = useCounter(6449800, 1000, 1200);
    const [showQR, setShowQR] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setShowQR(true), 2000);
        return () => clearTimeout(t);
    }, []);
    return (
        <div className="bg-white shadow-2xl rounded-sm overflow-hidden border border-slate-200 animate-[fadeInUp_.5s_ease-out]">
            <div className="text-center py-5 px-5 border-b-2 border-[#3D7A7A]">
                <p className="text-base font-bold text-[#2D5A5A] tracking-widest">ESTADO DE PAGO</p>
                <p className="text-[11px] font-bold tracking-[0.3em] text-slate-900 mt-1">LEGAV</p>
                <p className="text-[10px] text-slate-500 mt-0.5">FOLIO N° 0287 · 28-FEB-2026</p>
            </div>
            <div className="p-5 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">TecnoSur SpA</span></div>
                <div className="flex justify-between"><span className="text-slate-500">RUT</span><span className="font-mono">76.123.456-7</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Faena</span><span className="font-medium">Mina Cerro Verde</span></div>
                <div className="border-t border-slate-200 pt-2 mt-2 space-y-1.5">
                    <div className="flex justify-between"><span className="text-slate-500">Neto</span><span className="font-mono">{fmtCLP(monto)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">IVA 19%</span><span className="font-mono">{fmtCLP(iva)}</span></div>
                    <div className="flex justify-between font-bold pt-1 border-t border-slate-200"><span>Total</span><span className="text-[#3D7A7A] font-mono text-sm">{fmtCLP(total)}</span></div>
                </div>
            </div>
            <div className={`p-4 flex items-center gap-3 border-t bg-gradient-to-r from-[#3D7A7A]/5 to-[#5B9A9A]/5 transition-all duration-500 ${showQR ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
                <div className="w-14 h-14 bg-[#1A2B2B] rounded p-1 flex items-center justify-center relative overflow-hidden">
                    <QrCode size={44} className="text-white" />
                    <div className="absolute inset-x-0 h-0.5 bg-[#9DD5D5] animate-[scanline_1.5s_linear_infinite]" />
                </div>
                <div>
                    <p className="text-[10px] font-bold text-[#2D5A5A] uppercase tracking-widest">Aprobación del Cliente</p>
                    <p className="text-[10px] text-slate-600 leading-tight">Escanea el QR para aprobar o rechazar este EDP.</p>
                </div>
            </div>
        </div>
    );
};

const MockAprobacion = () => {
    const [phase, setPhase] = useState(0); // 0: scan, 1: opened, 2: tap, 3: done
    useEffect(() => {
        const t1 = setTimeout(() => setPhase(1), 1100);
        const t2 = setTimeout(() => setPhase(2), 2400);
        const t3 = setTimeout(() => setPhase(3), 3200);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, []);
    return (
        <div className="flex justify-center py-2">
            <div
                className="bg-slate-900 rounded-[2rem] p-2 shadow-2xl transition-transform duration-700"
                style={{ transform: phase >= 1 ? 'rotate(0deg) scale(1)' : 'rotate(-8deg) scale(.95)' }}
            >
                <div className="bg-white rounded-[1.6rem] w-56 overflow-hidden border-4 border-slate-800">
                    <div className="bg-slate-100 py-1.5 text-center text-[9px] text-slate-400 flex items-center justify-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" /> paysync.cl/edp/aprobar
                    </div>
                    {phase === 0 ? (
                        <div className="p-6 flex flex-col items-center">
                            <div className="w-32 h-32 bg-[#1A2B2B] rounded p-2 relative overflow-hidden">
                                <QrCode size={112} className="text-white" />
                                <div className="absolute inset-x-0 h-1 bg-[#9DD5D5] animate-[scanline_1.2s_linear_infinite] shadow-[0_0_12px_#9DD5D5]" />
                            </div>
                            <p className="text-[10px] text-slate-500 mt-3 animate-pulse">Escaneando…</p>
                        </div>
                    ) : (
                        <div className="p-3 animate-[fadeInUp_.4s_ease-out]">
                            <p className="text-[9px] uppercase text-slate-400 text-center tracking-widest">Aprobación EDP</p>
                            <p className="text-center text-[11px] font-bold text-[#2D5A5A] mt-1 tracking-widest">LEGAV</p>
                            <p className="text-center text-base font-bold mt-0.5">EDP N° 0287</p>
                            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1 text-[9px]">
                                <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-semibold">TecnoSur SpA</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Faena</span><span className="font-semibold">Cerro Verde</span></div>
                                <div className="flex justify-between pt-1 border-t border-slate-200"><span className="font-bold">Total</span><span className="font-bold text-[#3D7A7A]">$6.449.800</span></div>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 mt-3">
                                <div
                                    className={`border-2 rounded-lg py-3 text-center text-[9px] font-bold transition-all duration-300 ${
                                        phase >= 2
                                            ? 'border-emerald-500 bg-emerald-500 text-white scale-95 shadow-[0_0_20px_rgba(16,185,129,0.5)]'
                                            : 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                    }`}
                                >
                                    {phase >= 3 ? <Check size={16} className="mx-auto mb-0.5" /> : <CheckCircle2 size={14} className="mx-auto mb-0.5" />}
                                    {phase >= 3 ? 'APROBADO' : 'Aprobar'}
                                </div>
                                <div className="border border-slate-200 rounded-lg py-3 text-center text-slate-400 text-[9px] font-bold">
                                    <span className="text-base leading-none">×</span><br />Rechazar
                                </div>
                            </div>
                            {phase >= 3 && (
                                <p className="text-center text-[9px] text-emerald-600 font-semibold mt-2 animate-[fadeInUp_.3s_ease-out]">
                                    ¡Confirmado! Cierre bloqueado.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MockEstados = () => {
    const stages = [
        { l: 'En Curso', c: 'bg-blue-100 text-blue-700 border-blue-200' },
        { l: 'EDP Enviado', c: 'bg-purple-100 text-purple-700 border-purple-200' },
        { l: 'EDP APROBADO', c: 'bg-emerald-100 text-emerald-800 border-emerald-200 font-bold' },
        { l: 'Facturado', c: 'bg-amber-100 text-amber-800 border-amber-200' },
        { l: 'PAGADO', c: 'bg-emerald-200 text-emerald-900 border-emerald-300 font-bold' },
    ];
    const [active, setActive] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setActive((a) => (a < stages.length ? a + 1 : a)), 750);
        return () => clearInterval(id);
    }, []);
    return (
        <div className={baseCard + ' p-5'}>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-4 font-semibold flex items-center gap-2">
                <Target size={12} /> Flujo automático de estados
            </p>
            <div className="space-y-2.5">
                {stages.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div
                            className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-500 ${s.c} ${
                                i < active ? 'opacity-100 translate-x-0' : 'opacity-25 translate-x-2'
                            } ${i === active - 1 ? 'shadow-lg ring-2 ring-offset-2 ring-[#3D7A7A]/30' : ''}`}
                        >
                            {s.l}
                        </div>
                        {i < stages.length - 1 && (
                            <ArrowDown
                                size={14}
                                className={`text-slate-300 transition-opacity duration-500 ${i < active - 1 ? 'opacity-100' : 'opacity-25'}`}
                            />
                        )}
                    </div>
                ))}
            </div>
            {active >= stages.length && (
                <div className="mt-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 animate-[fadeInUp_.4s_ease-out]">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span className="text-emerald-800 text-xs font-semibold">Ciclo completo · sin intervención manual</span>
                </div>
            )}
        </div>
    );
};

const MockPago = () => {
    const total = 6449800;
    const pagado = useCounter(total, 1500, 400);
    const saldo = total - pagado;
    return (
        <div className={baseCard + ' p-5'}>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-4 font-semibold flex items-center gap-2">
                <Wallet size={12} /> Registrar Pago
            </p>
            <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Servicio</span><span className="font-medium">Asesoría · TecnoSur</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total facturado</span><span className="font-mono">{fmtCLP(total)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Método</span><span className="font-medium">Transferencia</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Fecha pago</span><span className="font-mono">15-04-2026</span></div>
                <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Recibido</span><span className="font-mono text-[#3D7A7A] font-bold">{fmtCLP(pagado)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#3D7A7A] to-emerald-500 rounded-full transition-all duration-100"
                            style={{ width: `${(pagado / total) * 100}%` }}
                        />
                    </div>
                </div>
                <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="font-bold">Saldo pendiente</span>
                    <span className={`font-bold font-mono ${saldo === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{fmtCLP(saldo)}</span>
                </div>
                {saldo === 0 && (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 animate-[fadeInUp_.4s_ease-out]">
                        <CheckCircle2 size={18} className="text-emerald-600" />
                        <span className="text-emerald-800 text-xs font-bold">Servicio marcado como PAGADO</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const MockDashboard = () => {
    const facturacion = useCounter(58400000, 1400, 100);
    const iva = useCounter(9300000, 1400, 250);
    const cobrar = useCounter(12800000, 1400, 400);
    const recuperado = useCounter(8400000, 1400, 550);
    const M = (n) => '$' + (n / 1000000).toFixed(1) + 'M';
    const kpis = [
        { l: 'Facturación', v: M(facturacion), c: '#3D7A7A', pct: 80 },
        { l: 'IVA', v: M(iva), c: '#D97706', pct: 60 },
        { l: 'Por cobrar', v: M(cobrar), c: '#DC2626', pct: 40 },
        { l: 'Recuperado', v: M(recuperado), c: '#059669', pct: 70 },
    ];
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                {kpis.map((k, i) => (
                    <div
                        key={i}
                        className="bg-white border border-slate-200 rounded-xl p-4 shadow-md animate-[fadeInUp_.4s_ease-out]"
                        style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'backwards' }}
                    >
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">{k.l}</p>
                        <p className="text-xl font-bold text-slate-900 font-mono mt-1">{k.v}</p>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full mt-2 overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-1000"
                                style={{ width: `${k.pct}%`, background: k.c, transitionDelay: `${300 + i * 100}ms` }}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <div className={baseCard + ' p-3'}>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Indicadores · Hoy</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                    {[['UF', '$38.124'], ['USD', '$945'], ['Cobre', '4.21']].map(([l, v]) => (
                        <div key={l} className="text-center">
                            <p className="text-[9px] text-slate-500">{l}</p>
                            <p className="font-bold text-slate-900 font-mono">{v}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Mock = ({ type }) => {
    if (type === 'config') return <MockConfig />;
    if (type === 'clientes') return <MockClientes />;
    if (type === 'servicios') return <MockServicios />;
    if (type === 'edp') return <MockEDP />;
    if (type === 'aprobacion') return <MockAprobacion />;
    if (type === 'estados') return <MockEstados />;
    if (type === 'pago') return <MockPago />;
    if (type === 'dashboard') return <MockDashboard />;
    return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main wizard
// ─────────────────────────────────────────────────────────────────────────────
export default function Aprende() {
    const [current, setCurrent] = useState(0);
    const [completed, setCompleted] = useState(() => new Set([0]));
    const sectionRef = useRef(null);

    const step = STEPS[current];
    const isLast = current === STEPS.length - 1;
    const isFirst = current === 0;
    const progress = ((current + 1) / STEPS.length) * 100;

    const goTo = (i) => {
        if (i < 0 || i >= STEPS.length) return;
        setCurrent(i);
        setCompleted((prev) => new Set([...prev, i]));
        if (sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="space-y-6">
            {/* Inline keyframes for screencasts */}
            <style>{`
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes scanline { 0% { top: 0; } 100% { top: 100%; } }
            `}</style>

            {/* HERO */}
            <div className="relative rounded-2xl bg-gradient-to-br from-[#0F1F1F] via-[#1A2B2B] to-[#2D5A5A] p-6 md:p-10 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#5B9A9A]/30 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#9DD5D5]/20 rounded-full blur-3xl" />
                <div className="relative max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full backdrop-blur text-xs text-white/90 mb-4">
                        <Sparkles size={13} className="text-[#9DD5D5]" /> Capacitación interactiva · {STEPS.length} pasos
                    </div>
                    <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight" data-testid="aprende-title">
                        Aprende en <span className="text-[#9DD5D5]">PaySync</span>
                    </h1>
                    <p className="text-white/80 mt-3 text-base md:text-lg leading-relaxed">
                        Avanza a tu ritmo: presiona <span className="font-semibold text-white">Siguiente</span> cuando estés listo.
                        Cada paso muestra un screencast animado para que veas exactamente qué pasa en la plataforma.
                    </p>
                </div>
            </div>

            {/* PROGRESS */}
            <div className="sticky top-0 z-30 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-white/95 backdrop-blur border-b border-slate-200">
                <div className="flex items-center justify-between mb-2 text-xs">
                    <span className="font-semibold text-slate-700">
                        Paso <span className="text-[#3D7A7A]">{current + 1}</span> de {STEPS.length}
                    </span>
                    <span className="text-slate-500">{Math.round(progress)}% completado</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#3D7A7A] via-[#5B9A9A] to-[#9DD5D5] rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                {/* Dots */}
                <div className="flex items-center gap-1.5 mt-3 overflow-x-auto pb-1">
                    {STEPS.map((s, i) => (
                        <button
                            key={i}
                            onClick={() => goTo(i)}
                            className={`flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all flex-shrink-0 ${
                                current === i
                                    ? 'bg-[#3D7A7A] text-white shadow-md'
                                    : completed.has(i)
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                            data-testid={`step-dot-${s.n}`}
                            title={s.title}
                        >
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                current === i ? 'bg-white/25' : completed.has(i) ? 'bg-emerald-500 text-white' : 'bg-white text-slate-500'
                            }`}>
                                {completed.has(i) && current !== i ? <Check size={9} /> : s.n}
                            </span>
                            <span className="hidden md:inline">{s.title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* CURRENT STEP */}
            <section
                ref={sectionRef}
                key={current}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start animate-[fadeInUp_.45s_ease-out]"
            >
                {/* Left: text */}
                <div className="order-2 lg:order-1">
                    <div className="flex items-center gap-3 mb-4">
                        <div
                            className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md"
                            style={{ backgroundColor: `${step.color}18`, border: `1px solid ${step.color}40` }}
                        >
                            <step.icon size={26} style={{ color: step.color }} />
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-widest font-bold" style={{ color: step.color }}>
                                Paso {step.n} de {STEPS.length}
                            </p>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="step-title">{step.title}</h2>
                        </div>
                    </div>
                    <p className="text-base text-slate-700 leading-relaxed mb-4 font-medium">{step.subtitle}</p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">{step.description}</p>

                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-2">
                            <CheckCircle2 size={14} className="text-emerald-600" />
                            Lo que vas a hacer
                        </p>
                        <ul className="space-y-1.5">
                            {step.checklist.map((it, j) => (
                                <li
                                    key={j}
                                    className="flex items-start gap-2 text-sm text-slate-700 animate-[fadeInUp_.4s_ease-out]"
                                    style={{ animationDelay: `${100 + j * 60}ms`, animationFillMode: 'backwards' }}
                                >
                                    <ArrowRight size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {step.tip && (
                        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                            <Bell size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs font-bold text-amber-800 uppercase tracking-widest mb-1">Tip</p>
                                <p className="text-sm text-amber-900 leading-relaxed">{step.tip}</p>
                            </div>
                        </div>
                    )}

                    <a
                        href={step.actionRoute}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-lg bg-[#3D7A7A] hover:bg-[#2D5A5A] text-white font-semibold text-sm transition-all shadow-sm hover:shadow-md"
                        data-testid={`step-action-${step.n}`}
                    >
                        {step.actionLabel}
                        <ExternalLink size={14} />
                    </a>
                    <p className="text-xs text-slate-400 mt-2">Se abre en otra pestaña para que practiques mientras lees.</p>
                </div>

                {/* Right: animated screencast */}
                <div className="order-1 lg:order-2 lg:sticky lg:top-32">
                    <div className="relative">
                        <div
                            className="absolute -inset-6 rounded-3xl blur-3xl opacity-30 -z-10"
                            style={{ background: `radial-gradient(circle, ${step.color} 0%, transparent 70%)` }}
                        />
                        <div className="absolute -top-3 -right-3 z-10 px-2.5 py-1 bg-slate-900 text-[#9DD5D5] text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                            Demo en vivo
                        </div>
                        {/* key forces remount → animations replay each step */}
                        <Mock key={current} type={step.mock} />
                    </div>
                </div>
            </section>

            {/* NAV BUTTONS */}
            <div className="sticky bottom-4 z-20 mx-auto max-w-3xl">
                <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-2xl p-3 md:p-4 flex items-center gap-3">
                    <button
                        onClick={() => goTo(current - 1)}
                        disabled={isFirst}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            isFirst
                                ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        data-testid="aprende-prev-btn"
                    >
                        <ChevronLeft size={16} /> Anterior
                    </button>

                    <div className="flex-1 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold hidden md:block">Avanzando</p>
                        <p className="text-sm font-semibold text-slate-700 truncate">{step.title}</p>
                    </div>

                    {isLast ? (
                        <a
                            href={ROUTES.dashboard}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-bold shadow-md transition-all"
                            data-testid="aprende-finish-btn"
                        >
                            <Smartphone size={16} /> ¡Empezar a usar!
                        </a>
                    ) : (
                        <button
                            onClick={() => goTo(current + 1)}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#3D7A7A] to-[#2D5A5A] hover:from-[#2D5A5A] hover:to-[#1A2B2B] text-white text-sm font-bold shadow-md transition-all"
                            data-testid="aprende-next-btn"
                        >
                            Siguiente <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
